import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { InlineNotice } from "@/components/common/InlineNotice";

export default function AuthPage({ mode = "login" }) {
  const [tab, setTab] = useState(mode);
  const [role, setRole] = useState("athlete");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [tos, setTos] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  async function submit(e) {
    e.preventDefault();
    if (tab === "signup") {
      if (password !== confirmPassword) return toast.error("Passwords do not match");
      if (password.length < 6) return toast.error("Password must be at least 6 characters");
      if (!tos) return toast.error("Please accept the Terms of Service and Privacy Policy");
    }
    setLoading(true);
    try {
      if (tab === "login") {
        const { data } = await api.post("/auth/login", { email, password });
        login(data.token, data.user);
        toast.success(`Welcome back, ${data.user.name}!`);
        nav(data.user.role === "admin" ? "/admin" : "/app");
      } else {
        const { data } = await api.post("/auth/signup", {
          email, password, name, role,
          phone: phone || null,
          tos_accepted: true,
        });
        login(data.token, data.user);
        toast.success(`Welcome to BallBoy, ${data.user.name}!`);
        nav(role === "athlete" ? "/athlete/verify" : "/app");
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Something went wrong"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8 bg-[radial-gradient(900px_circle_at_20%_10%,rgba(24,167,167,0.22),transparent_55%),radial-gradient(700px_circle_at_85%_0%,rgba(255,107,74,0.16),transparent_52%)]">
      <div className="w-full max-w-[420px]">
        <div className="text-center mb-5">
          <div className="mx-auto mb-3 h-12 w-12 rounded-xl bg-primary grid place-items-center text-primary-foreground font-bold text-xl bb-display">B</div>
          <h1 className="text-3xl font-semibold tracking-tight bb-display">BallBoy</h1>
          <p className="text-sm text-muted-foreground">Practice harder. Never chase a ball again.</p>
        </div>
        <Card>
          <CardHeader>
            <CardTitle className="text-lg bb-display">{tab === "login" ? "Log in" : "Create account"}</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={tab} onValueChange={setTab} data-testid="auth-mode-tabs">
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger value="login" data-testid="auth-tab-login">Log in</TabsTrigger>
                <TabsTrigger value="signup" data-testid="auth-tab-signup">Sign up</TabsTrigger>
              </TabsList>
              <TabsContent value={tab} className="mt-4">
                <form onSubmit={submit} className="space-y-3">
                  {tab === "signup" && (
                    <>
                      <div className="space-y-1.5">
                        <Label>Full legal name</Label>
                        <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="auth-name-input" placeholder="Jane Smith" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>I am a…</Label>
                        <Tabs value={role} onValueChange={setRole} data-testid="auth-role-tabs">
                          <TabsList className="grid grid-cols-2 w-full">
                            <TabsTrigger value="athlete" data-testid="auth-role-athlete">Athlete</TabsTrigger>
                            <TabsTrigger value="ballboy" data-testid="auth-role-ballboy">Ball Boy</TabsTrigger>
                          </TabsList>
                        </Tabs>
                      </div>
                      <div className="space-y-1.5">
                        <Label>Mobile phone (optional now — verify later)</Label>
                        <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 123 4567" data-testid="auth-phone-input" />
                      </div>
                    </>
                  )}
                  <div className="space-y-1.5">
                    <Label>Email</Label>
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="auth-email-input" autoComplete="email" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Password</Label>
                    <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6}
                      data-testid="auth-password-input" autoComplete={tab === "login" ? "current-password" : "new-password"} />
                  </div>
                  {tab === "signup" && (
                    <>
                      <div className="space-y-1.5">
                        <Label>Confirm password</Label>
                        <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={6}
                          data-testid="auth-confirm-password-input" autoComplete="new-password" />
                        {confirmPassword && password !== confirmPassword && (
                          <p className="text-xs text-destructive">Passwords do not match</p>
                        )}
                      </div>
                      <label className="flex items-start gap-2 text-xs text-muted-foreground">
                        <Checkbox checked={tos} onCheckedChange={(v) => setTos(!!v)} data-testid="auth-tos-checkbox" />
                        <span>
                          I agree to the{" "}
                          <Link to="/terms" target="_blank" className="underline text-primary">Terms of Service</Link>{" "}
                          and{" "}
                          <Link to="/privacy" target="_blank" className="underline text-primary">Privacy Policy</Link>.
                        </span>
                      </label>
                    </>
                  )}
                  <Button type="submit" className="w-full h-11" disabled={loading} data-testid="auth-submit-button">
                    {loading ? "Please wait…" : tab === "login" ? "Log in" : "Create account"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
            <InlineNotice tone="info" title="Demo credentials" className="mt-4">
              athlete@test.com / Test@1234<br />
              ballboy@test.com / Test@1234
            </InlineNotice>
          </CardContent>
        </Card>
        <p className="text-xs text-center text-muted-foreground mt-4">
          <Link to="/" className="underline">Back to landing</Link>
        </p>
      </div>
    </div>
  );
}
